import { routeChallengeMultiplier, routeReward } from '../../shared/map-identity.js';
import { liveChallengeDefinition } from '../../shared/live-challenges.js';
import { distanceToRouteEntry, getRacingPlan } from '../../shared/racing-line.js';
import type { MapDefinition, RaceEventMode, RacePhase, RaceSnapshot, RacerSnapshot } from '../../shared/types.js';
import { CAR_BY_ID } from '../../shared/cars.js';
import { citySetpieceProfile, signatureAbilityReward } from '../../shared/gameplay-fun.js';
import { vehicleSignatureFor } from '../../shared/vehicle-signatures.js';
import { locale, t, tn } from '../i18n/index.js';

export interface RaceDirectorCue {
  id: string;
  title: string;
  detail: string;
  tone: 'info' | 'attack' | 'danger' | 'reward';
  strength: number;
}

export interface RaceDirectorOptions {
  eventMode: RaceEventMode;
  raceLaps: number;
  nemesisNames?: readonly string[];
  initialPhase?: RacePhase;
}

/**
 * A tiny stateful race director. It observes authoritative snapshots and emits
 * sparse, deterministic beats; it never changes simulation state or allocates
 * work in the render loop.
 */
export class RaceDirector {
  private previousPhase: RaceSnapshot['phase'] | '' = '';
  private previousLap = 0;
  private previousPlace = 0;
  private lastCueAt = Number.NEGATIVE_INFINITY;
  private raceStartedAt = 0;
  private pressureBand = '';
  private readonly previousPlaces = new Map<string, number>();
  private readonly seen = new Set<string>();
  private readonly nemesisNames: Set<string>;
  private readonly lapLength: number;

  constructor(
    private readonly map: MapDefinition,
    private readonly options: RaceDirectorOptions,
  ) {
    this.nemesisNames = new Set((options.nemesisNames ?? []).map(normalizedName));
    this.lapLength = getRacingPlan(map).main.length;
    this.previousPhase = options.initialPhase ?? '';
  }

  observe(snapshot: RaceSnapshot, playerId: string, now = snapshot.serverTime): RaceDirectorCue | null {
    const player = snapshot.racers.find((racer) => racer.id === playerId);
    if (!player) return null;
    let cue: RaceDirectorCue | null = null;

    // Joining or reconnecting to a live race should not replay the green-flag
    // stinger. Start the pressure clock from this first observed live frame.
    if (snapshot.phase === 'racing' && this.previousPhase === 'racing' && this.raceStartedAt === 0) {
      this.raceStartedAt = now;
    }

    if (snapshot.phase === 'racing' && this.previousPhase !== 'racing') {
      this.raceStartedAt = now;
      const nemesis = snapshot.racers.find((racer) => (
        racer.id !== playerId && this.nemesisNames.has(normalizedName(racer.name))
      ));
      cue = this.once('launch', {
        id: nemesis ? 'nemesis-launch' : 'launch',
        title: nemesis
          ? t('SETTLE THE SCORE.')
          : this.options.eventMode === 'road-rage'
            ? t('HUNT THE MARKED CAR.')
            : this.options.eventMode === 'sprint' ? t('ONE LAP. NO ALIBIS.') : t('MAKE IT COUNT.'),
        detail: nemesis
          ? t('TARGET // {name}', { name: nemesis.name.toUpperCase() })
          : this.options.eventMode === 'road-rage'
            ? t('TAKEDOWNS SCORE · REPAIR GATES KEEP YOU ALIVE')
            : this.options.eventMode === 'elimination' ? t('LAST PLACE GETS CUT') : t('RACE CONTROL // GREEN'),
        tone: 'attack', strength: nemesis ? 1 : 0.72,
      });
    }

    if (!cue && snapshot.phase === 'racing' && snapshot.dynamicTrackEvent) {
      const event = snapshot.dynamicTrackEvent;
      const warning = event.phase === 'warning';
      const setpiece = citySetpieceProfile(event.setpiece);
      cue = this.once(`track-event:${event.eventId}:${event.phase}`, {
        id: `track-event-${event.phase}`,
        title: warning
          ? t(setpiece?.warning ?? 'ROAD HAZARD AHEAD.')
          : event.phase === 'active' ? t(setpiece?.name ?? 'LANE CLOSED.') : t('ROAD CLEARING.'),
        detail: t(event.side === 'right' ? '{event} // KEEP LEFT' : '{event} // KEEP RIGHT', {
          event: t(setpiece?.name ?? event.kind.replace('-', ' ').toUpperCase()),
        }),
        tone: event.phase === 'active' ? 'danger' : 'info',
        strength: event.phase === 'active' ? 0.92 : 0.68,
      });
    }

    if (!cue && snapshot.phase === 'racing') {
      const boss = snapshot.racers.find((racer) => racer.npc && racer.bossPhase && (racer.bossPhaseEventId ?? 0) > 0);
      if (boss) {
        const phaseCopy = boss.bossPhase === 'final'
          ? ['FINAL ACT.', 'THE BOSS HAS NOTHING LEFT TO HIDE']
          : boss.bossPhase === 'hunt'
            ? ['THE BOSS IS HUNTING.', 'EXPECT A FAST RESPONSE']
            : boss.bossPhase === 'pressure'
              ? ['PRESSURE PHASE.', 'BREAK THE DEFENSIVE LINE']
              : ['RIVAL BOSS ON GRID.', 'WATCH THE SIGNATURE MOVE'];
        cue = this.once(`boss-phase:${boss.id}:${boss.bossPhaseEventId}`, {
          id: 'boss-phase',
          title: `${boss.name.toUpperCase()} // ${t(phaseCopy[0]!)}`,
          detail: t(phaseCopy[1]!),
          tone: boss.bossPhase === 'final' ? 'danger' : 'attack',
          strength: boss.bossPhase === 'final' ? 1 : 0.82,
        });
      }
    }

    if (!cue && snapshot.phase === 'racing' && this.options.eventMode === 'road-rage') {
      const target = snapshot.racers.find((racer) => racer.roadRageTarget);
      if (target) {
        cue = this.once(`road-rage-target:${target.id}`, {
          id: 'road-rage-target',
          title: t('MARKED // {name}', { name: target.name.toUpperCase() }),
          detail: t('TAKEDOWN BONUS · REPAIR + NITRO PAYOUT'),
          tone: 'danger',
          strength: 0.9,
        });
      }
    }

    if (!cue && snapshot.phase === 'racing' && (player.heatLevel ?? 0) > 0) {
      const activeUnits = snapshot.racers.filter((racer) => racer.pursuit && racer.pursuitActive).length;
      cue = this.once(`heat:${player.heatLevel}:${activeUnits}`, {
        id: 'heat-escalation',
        title: activeUnits > 1 ? t('INTERCEPTOR PACK DEPLOYED.') : t('PURSUIT ENGAGED.'),
        detail: tn(activeUnits, 'HEAT {heat} // {n} UNIT ACTIVE', 'HEAT {heat} // {n} UNITS ACTIVE', {
          heat: player.heatLevel ?? 0,
        }),
        tone: 'danger',
        strength: Math.min(1, 0.65 + (player.heatLevel ?? 0) * 0.07),
      });
    }

    if (!cue && snapshot.phase === 'racing' && player.liveChallenge && player.liveChallengeEventId) {
      const definition = liveChallengeDefinition(player.liveChallenge);
      const status = player.liveChallengeStatus ?? 'inactive';
      cue = this.once(`contract:${player.liveChallengeEventId}:${status}`, {
        id: `live-contract-${status}`,
        title: status === 'complete'
          ? t('CONTRACT CLEARED.')
          : status === 'failed'
            ? t('CONTRACT MISSED.')
            : t(definition?.name ?? 'LIVE CONTRACT'),
        detail: status === 'active'
          ? definition ? t(definition.description).toUpperCase() : t('OPTIONAL OBJECTIVE')
          : status === 'complete' ? t('+STYLE // +NITRO') : t('NEXT OFFER INCOMING'),
        tone: status === 'complete' ? 'reward' : status === 'failed' ? 'danger' : 'info',
        strength: status === 'complete' ? 0.88 : 0.62,
      });
    }

    if (!cue && snapshot.phase === 'racing' && (player.sectorEventId ?? 0) > 0) {
      const delta = player.sectorDeltaMs ?? 0;
      cue = this.once(`sector:${player.sectorEventId}`, {
        id: 'sector-duel',
        title: delta <= 0 ? t('SECTOR WON.') : t('SECTOR LOST.'),
        detail: t('{delta} // RIVAL DELTA', { delta: `${delta <= 0 ? '−' : '+'}${(Math.abs(delta) / 1_000).toFixed(3)}` }),
        tone: delta <= 0 ? 'reward' : 'attack',
        strength: 0.68,
      });
    }

    if (!cue && snapshot.phase === 'racing') {
      const boss = snapshot.racers.find((racer) => (
        racer.npc
        && !racer.pursuit
        && racer.styleAction.startsWith('BOSS ')
        && racer.styleEventId > 0
      ));
      if (boss) {
        const detail = t(boss.styleAction === 'BOSS DRIFT' ? 'SIGNATURE DRIFT // HOLD YOUR LINE'
          : boss.styleAction === 'BOSS SHORTCUT' ? 'TRICK ROUTE // DO NOT FOLLOW BLIND'
            : boss.styleAction === 'BOSS ATTACK' ? 'CONTACT ATTACK // BRACE'
              : 'DEFENSIVE MOVE // FIND ANOTHER LINE');
        cue = this.once(`boss:${boss.id}:${boss.styleEventId}`, {
          id: 'boss-move',
          title: t('{name} MAKES A MOVE.', { name: boss.name.toUpperCase() }),
          detail,
          tone: 'attack',
          strength: 0.86,
        });
      }
    }

    if (
      !cue
      && snapshot.phase === 'racing'
      && player.signatureActive
      && player.styleEventId > 0
      && player.styleAction !== ''
      && player.styleAction !== 'CHAIN LOST'
    ) {
      const car = CAR_BY_ID.get(player.carId);
      if (car) {
        const signature = vehicleSignatureFor(car);
        const reward = signatureAbilityReward(signature.id, player.styleAction);
        if (reward.nitro > 0 || reward.boostMs > 0 || reward.repair > 0) {
          const payout = [
            reward.nitro > 0 ? t('+{n} NITRO', { n: reward.nitro }) : '',
            reward.boostMs > 0 ? t('ATTACK BOOST') : '',
            reward.repair > 0 ? t('+{n} INTEGRITY', { n: reward.repair }) : '',
          ].filter(Boolean).join(' · ');
          cue = this.once(`signature:${player.styleEventId}`, {
            id: 'signature-ability',
            title: t('{name} ACTIVATED.', { name: t(signature.name).toUpperCase() }),
            detail: payout,
            tone: 'reward',
            strength: 0.84,
          });
        }
      }
    }

    if (
      !cue
      && snapshot.phase === 'racing'
      && this.options.eventMode !== 'road-rage'
      && player.lap > this.previousLap
      && player.lap >= this.options.raceLaps
    ) {
      cue = this.once(`final-lap:${player.lap}`, {
        id: 'final-lap', title: this.options.raceLaps === 1 ? t('FINAL PUSH.') : t('FINAL LAP.'),
        detail: player.place === 1 ? t('PROTECT THE LEAD') : t('EVERY PASS COUNTS'),
        tone: 'attack', strength: 1,
      });
    }

    if (
      !cue
      && snapshot.phase === 'racing'
      && now - this.raceStartedAt >= 1_200
      && Math.abs(player.speed) >= 8
      && this.previousPlace > 0
      && player.place < this.previousPlace
    ) {
      const passed = this.passedRival(snapshot.racers, player);
      const nemesis = passed && this.nemesisNames.has(normalizedName(passed.name));
      cue = {
        id: nemesis ? 'nemesis-pass' : 'pass',
        title: nemesis ? t('RIVAL TAKEN.') : t('PASS CONFIRMED.'),
        detail: passed
          ? t('{name} // NOW BEHIND', { name: passed.name.toUpperCase() })
          : t('{place} PLACE', { place: ordinal(player.place) }),
        tone: 'reward',
        strength: nemesis ? 1 : 0.72,
      };
    }

    if (!cue && snapshot.phase === 'racing') cue = this.routeOpportunity(player);
    if (!cue && snapshot.phase === 'racing') cue = this.pressureCue(snapshot.racers, player, now);

    if (!cue && snapshot.phase === 'finished') {
      cue = this.once('finish', {
        id: 'finish',
        title: player.place === 1 ? t('DISTRICT OWNED.') : player.place <= 3 ? t('PODIUM LOCKED.') : t('RUN BANKED.'),
        detail: player.place === 1
          ? t('RACE CONTROL // WIN CONFIRMED')
          : t('{place} PLACE // STYLE {score}', { place: ordinal(player.place), score: Math.round(player.styleScore) }),
        tone: player.place <= 3 ? 'reward' : 'info',
        strength: player.place === 1 ? 1 : 0.65,
      });
    }

    this.previousPhase = snapshot.phase;
    this.previousLap = player.lap;
    this.previousPlace = player.place;
    this.previousPlaces.clear();
    for (const racer of snapshot.racers) {
      if (!racer.pursuit) this.previousPlaces.set(racer.id, racer.place);
    }
    if (cue) this.lastCueAt = now;
    return cue;
  }

  private routeOpportunity(player: RacerSnapshot): RaceDirectorCue | null {
    for (const route of this.map.features.alternateRoutes ?? []) {
      const distance = distanceToRouteEntry(this.map, player.progress, route);
      if (distance < 18 || distance > 72) continue;
      const key = `route:${player.lap}:${route.id}`;
      const payout = Math.round(routeReward(route) * routeChallengeMultiplier(route));
      const surface = t((route.surface ?? 'asphalt').toUpperCase());
      const purpose = route.kind === 'shortcut' ? t('TIME CUT') : t('STYLE JACKPOT');
      return this.once(key, {
        id: 'route',
        title: t(route.name).toUpperCase(),
        detail: t('{purpose} {kind} · {risk} · {surface} // +{payout} NITRO', {
          purpose,
          kind: t(route.kind.toUpperCase()),
          risk: t((route.risk ?? 'TECHNICAL').toUpperCase()),
          surface,
          payout,
        }),
        tone: route.risk === 'extreme' ? 'danger' : 'info',
        strength: route.risk === 'extreme' ? 0.88 : 0.62,
      });
    }
    return null;
  }

  private pressureCue(racers: readonly RacerSnapshot[], player: RacerSnapshot, now: number): RaceDirectorCue | null {
    if (now - this.raceStartedAt < 9_000 || now - this.lastCueAt < 8_000) return null;
    const playerScore = raceScore(player);
    let closest: RacerSnapshot | null = null;
    let closestGap = Number.POSITIVE_INFINITY;
    for (const rival of racers) {
      if (rival.id === player.id || rival.pursuit || rival.finishedAt !== null) continue;
      const gap = (raceScore(rival) - playerScore) * this.lapLength;
      if (Math.abs(gap) >= Math.abs(closestGap)) continue;
      closest = rival;
      closestGap = gap;
    }
    const inDanger = this.options.eventMode === 'elimination' && player.place === racers.length;
    const sprintPush = this.options.eventMode === 'sprint' && player.progress >= 0.68;
    const band = inDanger ? 'elimination-danger'
      : sprintPush ? 'sprint-push'
        : closest && Math.abs(closestGap) <= 32 ? closestGap > 0 ? `hunt:${closest.id}` : `defend:${closest.id}`
          : '';
    if (!band || band === this.pressureBand) return null;
    this.pressureBand = band;
    if (inDanger) return {
      id: 'elimination-danger', title: t('DANGER ZONE.'), detail: t('MAKE THE PASS OR YOU ARE OUT'), tone: 'danger', strength: 1,
    };
    if (sprintPush) return {
      id: 'sprint-push', title: t('NO SECOND LAP.'), detail: t('EMPTY THE NITRO'), tone: 'attack', strength: 0.9,
    };
    if (!closest) return null;
    return closestGap > 0
      ? { id: 'hunt', title: t('RIVAL IN RANGE.'), detail: `${closest.name.toUpperCase()} // ${Math.max(1, Math.round(closestGap))} M`, tone: 'attack', strength: 0.72 }
      : { id: 'defend', title: t('PRESSURE BEHIND.'), detail: t('{name} // HOLD YOUR LINE', { name: closest.name.toUpperCase() }), tone: 'danger', strength: 0.7 };
  }

  private passedRival(racers: readonly RacerSnapshot[], player: RacerSnapshot): RacerSnapshot | null {
    let target: RacerSnapshot | null = null;
    let previousTargetPlace = Number.NEGATIVE_INFINITY;
    for (const rival of racers) {
      if (rival.id === player.id || rival.pursuit || rival.place <= player.place) continue;
      const previous = this.previousPlaces.get(rival.id);
      if (previous === undefined || previous >= this.previousPlace || previous <= previousTargetPlace) continue;
      previousTargetPlace = previous;
      target = rival;
    }
    return target;
  }

  private once(key: string, cue: RaceDirectorCue): RaceDirectorCue | null {
    if (this.seen.has(key)) return null;
    this.seen.add(key);
    return cue;
  }
}

function raceScore(racer: Pick<RacerSnapshot, 'lap' | 'progress'>): number {
  return Math.max(0, racer.lap - 1) + racer.progress;
}

function normalizedName(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function ordinal(value: number): string {
  if (locale() === 'de') return `${value}.`;
  const suffix = value % 10 === 1 && value % 100 !== 11 ? 'ST'
    : value % 10 === 2 && value % 100 !== 12 ? 'ND'
      : value % 10 === 3 && value % 100 !== 13 ? 'RD' : 'TH';
  return `${value}${suffix}`;
}
